import { expected, RouteItem } from '@aloft/toolkit-shared';

import { useLayoutEffect } from 'react';
import { Navigate, useNavigationType, useParams } from 'react-router';

import {
  getAlertTriggerForRoute,
  SubnavAlertTriggerRoute,
} from '../../components/MainHeader/HeaderNav/alertTriggerUtils.ts';
import { routeToStepMap } from '../../hooks/validation/consts.ts';
import {
  getActiveAlertsForRoute,
  useAppraisifyNavigation,
} from '../../hooks/validation/useAppraisifyNavigation.ts';
import { useAlerts } from '../../modules/referenceView/AlertsSheet/hooks/useAlerts.tsx';
import { selectCommentaryV2 } from '../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectIsInspectifyOrder } from '../../redux/features/selectors/orderSelectors.ts';
import {
  processAlertTrigger,
  processAlertsForRoute,
  retriggerAlertToasts,
} from '../../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';

export const AppraisifyStepGuard = ({
  routeSegment,
  children,
}: React.PropsWithChildren<{ routeSegment: string }>) => {
  const isInspectifyOrder = useAppSelector(selectIsInspectifyOrder);
  const alertsCommentary = useAppSelector(selectCommentaryV2).alerts;
  const { isStepEnabled, getLatestAllowedRoute, getEarliestRouteWithActiveAlertsBeforeTarget } =
    useAppraisifyNavigation();
  const dispatch = useAppDispatch();
  const alerts = useAlerts();
  const { orderId } = useParams();
  const navigationType = useNavigationType();

  const step = routeToStepMap[routeSegment as RouteItem];
  const isValidationBlocked = Boolean(step && !isStepEnabled(step));
  const blockingRoute = getEarliestRouteWithActiveAlertsBeforeTarget(
    alerts,
    routeSegment as RouteItem,
  );
  const isBlockedByPriorPageAlerts = Boolean(
    isInspectifyOrder && navigationType !== 'POP' && blockingRoute,
  );
  const alertTrigger = getAlertTriggerForRoute(routeSegment as SubnavAlertTriggerRoute);

  useLayoutEffect(() => {
    if (!isInspectifyOrder) {
      return;
    }
    if (isBlockedByPriorPageAlerts && blockingRoute) {
      const blockingRouteAlertTypes = getActiveAlertsForRoute(
        alerts,
        blockingRoute,
        alertsCommentary,
      ).map((alert) => alert.type);
      dispatch(
        processAlertsForRoute({
          route: blockingRoute,
          alertsPerSection: alerts,
          alertsCommentary,
        }),
      );
      if (blockingRouteAlertTypes.length > 0) {
        dispatch(retriggerAlertToasts(blockingRouteAlertTypes));
      }
      return;
    }
    if (!isValidationBlocked || !alertTrigger) {
      return;
    }
    dispatch(
      processAlertTrigger({
        trigger: alertTrigger,
        alertsPerSection: alerts,
        alertsCommentary,
      }),
    );
  }, [
    alertTrigger,
    alerts,
    alertsCommentary,
    dispatch,
    blockingRoute,
    isBlockedByPriorPageAlerts,
    isInspectifyOrder,
    isValidationBlocked,
  ]);

  if (!isInspectifyOrder) {
    return children;
  }

  if (isBlockedByPriorPageAlerts && blockingRoute) {
    return <Navigate to={`/order/${expected(orderId)}/paired-cost/${blockingRoute}`} replace />;
  }

  if (!step || !isValidationBlocked) {
    return children;
  }

  return (
    <Navigate to={`/order/${expected(orderId)}/paired-cost/${getLatestAllowedRoute()}`} replace />
  );
};
